import { ACTOR_LABEL, useFlywheel, type FlywheelActor } from '../lib/flywheel.js';

interface Props {
  onContinue: () => void;
  /** "intro" shown on first entry; "closing" shown when revisited from the topbar pill */
  variant: 'intro' | 'closing';
}

/**
 * Full-screen flywheel view. Three nodes in a triangle, three flowing
 * arcs, narration printed above so Koh can read it aloud during the
 * 30-second opening of the demo.
 *
 * Used both for the opening (variant="intro") — first thing after the
 * passcode gate — and the closing (variant="closing") — when the user
 * clicks the indicator pill in the topbar to "see the loop again."
 */
const NODE_LAYOUT: Record<FlywheelActor, { cx: number; cy: number; angle: number }> = {
  // Triangle, viewBox 0 0 600 460. alumni top-left, candidate top-right,
  // operator bottom-center.
  alumni: { cx: 130, cy: 140, angle: 210 },
  candidate: { cx: 470, cy: 140, angle: 330 },
  operator: { cx: 300, cy: 380, angle: 90 },
};

// Arc paths: a smooth curve from one node to the next, with arrow at end.
// Curves bow slightly outward so the triangle "feels" rotational.
const ARCS: Array<{
  from: FlywheelActor;
  to: FlywheelActor;
  d: string;
  midpoint: { x: number; y: number };
  flowLabel: string;
}> = [
  {
    from: 'alumni',
    to: 'candidate',
    d: 'M 165 130 Q 300 60 435 130',
    midpoint: { x: 300, y: 80 },
    flowLabel: '体験を残す',
  },
  {
    from: 'candidate',
    to: 'operator',
    d: 'M 460 175 Q 480 320 335 380',
    midpoint: { x: 460, y: 290 },
    flowLabel: '関心が見える',
  },
  {
    from: 'operator',
    to: 'alumni',
    d: 'M 265 380 Q 120 320 140 175',
    midpoint: { x: 140, y: 290 },
    flowLabel: '次の回を整える',
  },
];

const ACTOR_DESCRIPTIONS: Record<FlywheelActor, string> = {
  alumni: '帰国後、自分の言葉で振り返りを残す',
  candidate: 'これから行く後輩が、先輩と話す',
  operator: '運営が、声を集めてプログラムを磨く',
};

export function FlywheelIntro({ onContinue, variant }: Props) {
  const { active } = useFlywheel();

  return (
    <div className="main">
      <div className="container container-wide flywheel-stage">
        <p className="muted flywheel-eyebrow">D-COMPASS × SENPAI</p>
        <h1 className="flywheel-title">
          {variant === 'intro' ? '体験は、止まらず巡る。' : 'もう一度、ループを見る。'}
        </h1>
        <p className="lede flywheel-narration">
          先輩が残した体験が、次の候補者の準備を支え、
          そこから生まれる声が、次の回をより良くしていく。
          3つの動きが噛み合うと、留学体験は「終わる」のではなく「続く」資産になります。
        </p>

        <svg viewBox="0 0 600 460" className="flywheel-svg" aria-hidden>
          {/* Arcs (drawn first so nodes sit on top) */}
          {ARCS.map((arc) => {
            const isActive = active && active.actor === arc.from && active.flowsTo === arc.to;
            return (
              <g key={`${arc.from}-${arc.to}`}>
                <path
                  d={arc.d}
                  stroke={isActive ? 'var(--accent)' : 'var(--ink-muted)'}
                  strokeWidth={isActive ? 3 : 1.5}
                  strokeOpacity={isActive ? 1 : 0.35}
                  fill="none"
                  markerEnd={`url(#arrow-${isActive ? 'on' : 'off'})`}
                  style={{
                    transition: 'stroke 0.4s, stroke-width 0.4s, stroke-opacity 0.4s',
                  }}
                />
                <text
                  x={arc.midpoint.x}
                  y={arc.midpoint.y}
                  fill={isActive ? 'var(--accent)' : 'var(--ink-muted)'}
                  fillOpacity={isActive ? 1 : 0.7}
                  fontSize={14}
                  fontWeight={isActive ? 600 : 500}
                  textAnchor="middle"
                  style={{ transition: 'fill 0.3s, fill-opacity 0.3s' }}
                >
                  {arc.flowLabel}
                </text>
              </g>
            );
          })}

          {/* Marker definitions for arrowheads */}
          <defs>
            <marker
              id="arrow-off"
              viewBox="0 0 10 10"
              refX={7}
              refY={5}
              markerWidth={7}
              markerHeight={7}
              orient="auto-start-reverse"
            >
              <path d="M 0 0 L 10 5 L 0 10 z" fill="var(--ink-muted)" fillOpacity={0.35} />
            </marker>
            <marker
              id="arrow-on"
              viewBox="0 0 10 10"
              refX={7}
              refY={5}
              markerWidth={8}
              markerHeight={8}
              orient="auto-start-reverse"
            >
              <path d="M 0 0 L 10 5 L 0 10 z" fill="var(--accent)" />
            </marker>
          </defs>

          {/* Three nodes */}
          {(['alumni', 'candidate', 'operator'] as FlywheelActor[]).map((actor) => {
            const pos = NODE_LAYOUT[actor];
            const isActive = active?.actor === actor;
            return (
              <g key={actor} transform={`translate(${pos.cx}, ${pos.cy})`}>
                <circle
                  r={isActive ? 50 : 44}
                  fill={isActive ? 'var(--accent)' : 'var(--surface)'}
                  stroke={isActive ? 'var(--accent)' : 'var(--line)'}
                  strokeWidth={2}
                  style={{
                    transition: 'r 0.4s, fill 0.4s, stroke 0.4s',
                    filter: isActive ? 'drop-shadow(0 0 12px var(--accent-soft))' : 'none',
                  }}
                />
                <text
                  y={6}
                  fill={isActive ? 'white' : 'var(--ink)'}
                  fontSize={16}
                  fontWeight={600}
                  textAnchor="middle"
                  style={{ transition: 'fill 0.3s' }}
                >
                  {ACTOR_LABEL[actor].split(' ')[0]}
                </text>
                <text
                  y={70}
                  fill="var(--ink-soft)"
                  fontSize={12}
                  textAnchor="middle"
                  style={{ maxWidth: 200 }}
                >
                  {ACTOR_DESCRIPTIONS[actor]}
                </text>
              </g>
            );
          })}
        </svg>

        <div className="flywheel-cta">
          <button type="button" className="btn" onClick={onContinue}>
            {variant === 'intro' ? 'はじめる' : '戻る'}
          </button>
        </div>
      </div>
    </div>
  );
}
